import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGenerationFeedback,
  getGenerationJob,
  listGenerationArtifacts,
  upsertGenerationFeedback,
  type GenerationArtifactRecord,
  type GenerationArtifactType,
  type GenerationFeedbackRating,
} from '@/services/templateSystemApi';
import { queryKeys } from '@/lib/queryKeys';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState, {
  ContentLoadError,
  ContentNotFound,
} from '@/components/common/ContentErrorState';
import FormMessage from '@/components/common/FormMessage';
import { getUserFriendlyMessage } from '@/services/apiError';

const artifactOrder: GenerationArtifactType[] = [
  'lesson_plan_markdown',
  'assessment_set',
  'topology_spec',
  'slide_spec',
  'slides_html',
  'slides_beamer_tex',
];

export default function GenerationJobResultPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [copiedArtifactId, setCopiedArtifactId] = useState<string>('');
  const [rating, setRating] = useState<GenerationFeedbackRating>('like');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const jobQuery = useQuery({
    queryKey: queryKeys.generationJobs.detail(id ?? ''),
    queryFn: () => getGenerationJob(id ?? ''),
    enabled: Boolean(id),
    retry: 1,
    refetchInterval: (query) => {
      const status = (query.state.data as { status?: string } | undefined)?.status;
      return status === 'queued' || status === 'running' ? 3000 : false;
    },
  });

  const artifactsQuery = useQuery({
    queryKey: queryKeys.generationJobs.artifacts(id ?? ''),
    queryFn: () => listGenerationArtifacts(id ?? ''),
    enabled: Boolean(id),
    retry: 1,
  });

  const feedbackQuery = useQuery({
    queryKey: queryKeys.generationJobs.feedback(id ?? ''),
    queryFn: () => getGenerationFeedback(id ?? ''),
    enabled: Boolean(id),
    retry: 1,
  });

  const feedbackMutation = useMutation({
    mutationFn: (payload: { rating: GenerationFeedbackRating; feedbackText?: string }) =>
      upsertGenerationFeedback(id ?? '', payload),
    onSuccess: async (data) => {
      setRating(data.rating);
      setFeedbackText(data.feedbackText ?? '');
      setFeedbackMessage('反馈已保存。');
      await queryClient.invalidateQueries({ queryKey: queryKeys.generationJobs.feedback(id ?? '') });
    },
    onError: (error) => {
      setFeedbackMessage(getUserFriendlyMessage(error, '保存反馈失败'));
    },
  });

  useEffect(() => {
    if (!feedbackQuery.data) {
      return;
    }
    setRating(feedbackQuery.data.rating);
    setFeedbackText(feedbackQuery.data.feedbackText ?? '');
  }, [feedbackQuery.data]);

  const submitFeedback = () => {
    setFeedbackMessage('');
    feedbackMutation.mutate({
      rating,
      feedbackText: feedbackText.trim() || undefined,
    });
  };

  const orderedArtifacts = useMemo(() => {
    const map = new Map((artifactsQuery.data ?? []).map((artifact) => [artifact.artifactType, artifact]));
    return artifactOrder.map((type) => map.get(type)).filter((item): item is GenerationArtifactRecord => Boolean(item));
  }, [artifactsQuery.data]);

  if (jobQuery.isLoading) {
    return <PageLoading message='生成结果加载中…' />;
  }

  if (!id) {
    return (
      <div className='max-w-[1080px] mx-auto px-6 py-12'>
        <ContentNotFound />
      </div>
    );
  }

  if (jobQuery.isError) {
    return (
      <div className='max-w-[1080px] mx-auto px-6 py-12'>
        <ContentApiErrorState error={jobQuery.error} onRetry={() => void jobQuery.refetch()} />
      </div>
    );
  }

  if (!jobQuery.data) {
    return (
      <div className='max-w-[1080px] mx-auto px-6 py-12'>
        <ContentLoadError onRetry={() => void jobQuery.refetch()} />
      </div>
    );
  }

  const job = jobQuery.data;

  return (
    <div className='max-w-[1180px] mx-auto px-6 py-12'>
      <div className='flex items-center justify-between gap-3 mb-6'>
        <Link to='/generation-jobs' className='btn-ghost'>
          ‹ 返回历史
        </Link>
        <button
          type='button'
          className='btn btn-secondary'
          onClick={() => {
            void jobQuery.refetch();
            void artifactsQuery.refetch();
            void feedbackQuery.refetch();
          }}
        >
          刷新
        </button>
      </div>

      <h1 className='font-semibold text-apple-near-black mb-2 text-[2rem] leading-tight tracking-tight'>生成任务结果</h1>
      <p className='text-apple-gray mb-6 text-[0.9375rem]'>
        模板: {job.template?.name ?? job.templateId} · 状态: {job.status}
      </p>

      <section className='bg-white rounded-2xl p-5 shadow-card-sm mb-6'>
        <h2 className='font-semibold text-[1.0625rem] text-apple-near-black mb-3'>任务摘要</h2>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-[0.875rem]'>
          <InfoRow label='Job ID' value={job.id} />
          <InfoRow label='Template Version' value={String(job.templateVersion)} />
          <InfoRow label='Provider' value={job.llmProvider?.name ?? 'mock'} />
          <InfoRow label='Model' value={job.llmModel?.modelId ?? 'mock-template-v1'} />
          <InfoRow label='Prompt Source' value={job.effectiveTemplateSource ?? 'site_default'} />
          <InfoRow label='Latency (ms)' value={job.latencyMs ? String(job.latencyMs) : '-'} />
          <InfoRow label='创建时间' value={new Date(job.createdAt).toLocaleString('zh-CN', { hour12: false })} />
          <InfoRow label='完成时间' value={job.completedAt ? new Date(job.completedAt).toLocaleString('zh-CN', { hour12: false }) : '-'} />
        </div>

        {job.errorText ? <p className='mt-3 text-[#ff3b30] text-[0.875rem]'>错误: {job.errorText}</p> : null}
      </section>

      <section className='bg-white rounded-2xl p-5 shadow-card-sm mb-6'>
        <h2 className='font-semibold text-[1.0625rem] text-apple-near-black mb-3'>产物</h2>
        {artifactsQuery.isLoading ? <p className='text-apple-gray text-[0.875rem]'>加载产物中…</p> : null}
        {artifactsQuery.isError ? (
          <ContentApiErrorState error={artifactsQuery.error} onRetry={() => void artifactsQuery.refetch()} />
        ) : null}

        <div className='space-y-4'>
          {orderedArtifacts.map((artifact) => (
            <article key={artifact.id} className='border border-apple-border rounded-xl p-4'>
              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3'>
                <div>
                  <h3 className='font-medium text-apple-near-black'>{artifact.artifactType}</h3>
                  <p className='text-apple-gray text-[0.75rem]'>{artifact.title}</p>
                </div>
                <div className='flex gap-2'>
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={() => {
                      const text = artifact.contentText || JSON.stringify(artifact.contentJson ?? {}, null, 2);
                      void navigator.clipboard.writeText(text);
                      setCopiedArtifactId(artifact.id);
                      setTimeout(() => {
                        setCopiedArtifactId('');
                      }, 800);
                    }}
                  >
                    {copiedArtifactId === artifact.id ? '已复制' : '复制'}
                  </button>
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={() => downloadArtifact(artifact)}
                  >
                    下载
                  </button>
                </div>
              </div>

              {artifact.artifactType === 'slides_html' && artifact.contentText ? (
                <iframe
                  title={artifact.title}
                  srcDoc={artifact.contentText}
                  className='w-full h-[420px] border border-apple-border rounded-lg'
                />
              ) : null}

              {artifact.artifactType !== 'slides_html' ? (
                <pre className='text-[0.75rem] text-apple-near-black bg-apple-light-gray rounded-lg p-3 overflow-auto whitespace-pre-wrap max-h-[420px]'>
                  {artifact.contentText || JSON.stringify(artifact.contentJson ?? {}, null, 2)}
                </pre>
              ) : null}
            </article>
          ))}

          {!artifactsQuery.isLoading && orderedArtifacts.length === 0 ? (
            <p className='text-apple-gray text-[0.875rem]'>当前还没有可展示的产物。</p>
          ) : null}
        </div>
      </section>

      <section className='bg-white rounded-2xl p-5 shadow-card-sm mb-6 space-y-3'>
        <h2 className='font-semibold text-[1.0625rem] text-apple-near-black'>生成反馈</h2>
        {feedbackMessage ? (
          <FormMessage
            tone={feedbackMessage.includes('失败') ? 'error' : 'success'}
            message={feedbackMessage}
          />
        ) : null}
        {feedbackQuery.isError ? (
          <ContentApiErrorState error={feedbackQuery.error} onRetry={() => void feedbackQuery.refetch()} />
        ) : null}
        <div className='flex flex-wrap gap-2'>
          <button
            type='button'
            className={rating === 'like' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setRating('like')}
          >
            Like
          </button>
          <button
            type='button'
            className={rating === 'dislike' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setRating('dislike')}
          >
            Dislike
          </button>
        </div>
        <div className='space-y-2'>
          <textarea
            className='input min-h-[120px]'
            placeholder='可选：填写具体反馈，例如“题目过易”或“拓扑不合理”等'
            value={feedbackText}
            onChange={(event) => setFeedbackText(event.target.value)}
          />
          <button
            type='button'
            className='btn btn-primary'
            disabled={feedbackMutation.isPending}
            onClick={submitFeedback}
          >
            {feedbackMutation.isPending ? '提交中…' : '保存反馈'}
          </button>
        </div>
      </section>

      <section className='bg-white rounded-2xl p-5 shadow-card-sm'>
        <h2 className='font-semibold text-[1.0625rem] text-apple-near-black mb-3'>编译后的 Prompt</h2>
        <pre className='text-[0.75rem] text-apple-near-black bg-apple-light-gray rounded-lg p-3 overflow-auto whitespace-pre-wrap max-h-[320px]'>
          {JSON.stringify(job.compiledPromptJson ?? {}, null, 2)}
        </pre>
      </section>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div>
      <p className='text-apple-gray text-[0.75rem] mb-1'>{label}</p>
      <p className='text-apple-near-black break-all'>{value}</p>
    </div>
  );
}

function downloadArtifact(artifact: GenerationArtifactRecord) {
  const raw = artifact.contentText || JSON.stringify(artifact.contentJson ?? {}, null, 2);
  const blob = new Blob([raw], { type: artifact.mimeType || 'text/plain' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = artifact.title || `${artifact.artifactType}.txt`;
  anchor.click();
  URL.revokeObjectURL(url);
}
